import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { DataGrid } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  getUserManagementRoleList,
  createRole,
  selectUserManagementRolesList,
  selectIsLoadingUserManagementRolesList,
} from '../../store/user-management-roles.slice';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../../components/TableToolbar';
import { roleListBreadcrumb } from '../../user-management.breadcrumbs';

const MESSAGES = {
  CreateSuccess: 'Yay! A new role has been created successfully.',
  CreateFailure: 'Oops! Something went wrong while creating the role.',
  UpdateSuccess: 'Yay! Role details has been updated successfully.',
  UpdateFailure: 'Oops! Something went wrong while updating the role details.',
  DeleteSuccess: 'Yay! Role has been deleted successfully.',
  DeleteFailure: 'Oops! Something went wrong while deleting the role.',
  FormFieldRequiredError: 'This is a required field',
  FormFieldRoleNameLengthError: 'Role name should be longer than 4 characters',
};

function Toolbar({ openNewUserDialog, permissions }) {
  return (
    <TableToolbar>
      <Button
        variant="contained"
        disabled={!permissions?.canCreateUserManagementRole}
        onClick={openNewUserDialog}
        startIcon={<AddIcon />}>
        New
      </Button>
    </TableToolbar>
  );
}

export default function UserManagementRoleList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const roleList = useSelector(selectUserManagementRolesList);
  const isLoadingRoleList = useSelector(selectIsLoadingUserManagementRolesList);
  const [openDialog, setOpenDialog] = useState({ show: false });

  const [isNewRoleDialogOpen, setIsNewRoleDialogOpen] = useState(false);

  useEffect(() => {
    if (!permissions?.canViewUserManagementRole) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumbs
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(roleListBreadcrumb));
  }, [dispatch]);

  // API: Get User List
  useEffect(() => {
    dispatch(getUserManagementRoleList());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    setIsNewRoleDialogOpen(false);
  }, []);

  const createNewRole = (values) => {
    const { roleName } = values;

    const payload = {
      roleName,
    };

    dispatch(createRole(payload)).then(({ payload: response }) => {
      if (!response || response.status !== 'Created') {
        const message = response?.message || MESSAGES.CreateFailure;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return { [FORM_ERROR]: message };
      }
      dispatch(showSuccessMessage(MESSAGES.CreateSuccess));
      dispatch(getUserManagementRoleList());

      return resetForm();
    });
  };

  const validateNewRole = (values) => {
    const errors = {};

    if (!values.roleName) {
      errors.roleName = MESSAGES.FormFieldRequiredError;
    } else {
      const isRoleNameLengthValid = values.roleName.length > 4;
      if (!isRoleNameLengthValid) {
        errors.roleName = MESSAGES.FormFieldRoleNameLengthError;
      }
    }

    return errors;
  };

  const getGridComponents = () => ({
    Toolbar: () =>
      Toolbar({
        openNewUserDialog: () => {
          setIsNewRoleDialogOpen(true);
        },
        permissions,
      }),
  });

  const gridComponentProps = {
    toolbar: {
      csvOptions: { disableToolbarButton: true },
      printOptions: { disableToolbarButton: true },
      showQuickFilter: false,
      quickFilterProps: { debounceMs: 500 },
    },
  };

  const getGridColumns = () => [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'roleName',
      headerName: 'Role Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
    },
  ];

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="User Management - Roles"
      description="List of roles in the system"
      pageReady>
      <DataGrid
        autoHeight
        density="comfortable"
        loading={isLoadingRoleList}
        rows={roleList}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        components={getGridComponents()}
        componentsProps={gridComponentProps}
        columns={getGridColumns()}
      />
      <Dialog
        fullWidth
        open={isNewRoleDialogOpen}>
        <DialogTitle>Add New Role</DialogTitle>
        <Form
          onSubmit={createNewRole}
          validate={validateNewRole}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({ submitError, handleSubmit, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Role Name"
                    name="roleName"
                    required
                    fullWidth
                  />
                </Box>

                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 30 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Box>
                  <Button
                    variant="text"
                    onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      !permissions?.canCreateUserManagementRole || submitting
                    }>
                    Submit
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
